uniform float u_amplitude;

uniform int u_state;
uniform float u_state_start;
uniform int u_transition_type;
uniform float u_transition_start;
uniform float u_duration;
uniform float u_time;
uniform vec2 u_resolution;

uniform bool u_stop_session;
uniform float u_stop_time;

uniform float u_big_ball_size;
uniform float u_small_ball_size;
uniform float u_loading_radius;
uniform float u_orbit_radius;

uniform float u_opacity;

float antialiasing(float dist) {
    float edgeWidth = 2.0;
    float pixelSize = 1.0 / u_resolution.y;
    float halfWidth = edgeWidth * pixelSize;
    return smoothstep(-halfWidth, halfWidth, dist);
}

//----------------------------------CONSTANTS----------------------------------
const float PI = 3.14159265358979323846;
const int CIRCLE_COUNT = 4;


struct circle {
    vec2 position;
    float radius;
    vec4 color;
};

float circleSDF(float radius, vec2 point, vec2 center) {
    return length(point - center) - radius;
}

float MetaBallSDF(circle c, vec2 uv) {
    return c.radius / length(uv - c.position);
}

float springEffect(float t, float strength, float frequency, float decay) {
    return strength * exp(-decay * t) * sin(frequency * t * PI * 2.0);
}

// Smooth minimum — blends two SDF shapes with a rounded join
float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
}

// Rounded rectangle SDF — b is half-size, r is corner radius
float roundedBoxSDF(vec2 p, vec2 b, float r) {
    vec2 q = abs(p) - b + r;
    return min(max(q.x, q.y), 0.0) + length(max(q, vec2(0.0))) - r;
}


// -------------------------------END_SESSION----------------------------------
float noise(vec2 p) {
    return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453 + u_time);
}

float ramp(float y, float start, float end) {
    float inside = step(start, y) - step(end, y);
    float fact = (y - start) / (end - start) * inside;
    return (1. - fact) * inside;

}

float stripes(vec2 uv) {

    float noi = noise(uv * vec2(0.5, 1.) + vec2(1., 3.));
    return ramp(mod(uv.y * 4. + u_time / 2. + sin(u_time + sin(u_time * 0.63)), 1.), 0.5, 0.6) * noi;
}

vec2 screenDistort(vec2 uv) {
    uv -= vec2(.5, .5);
    uv = uv * 1.2 * (1. / 1.2 + 2. * uv.x * uv.x * uv.y * uv.y);
    uv += vec2(.5, .5);
    return uv;
}

float rectangleSDF(float height, float width, vec2 uv) {
    vec2 halfSize = vec2(width, height);
    vec2 d = abs(uv) - halfSize;
    return max(d.x, d.y);
}

float bloom(float dist, float intensity) {
    return exp(-dist * intensity);
}

//---------------------------------TRANSITIONS---------------------------------
float init_transition(vec2 uv, float t) {
    const int circle_count = CIRCLE_COUNT;
    float orbit_radius = u_orbit_radius;

    float initial_angle = PI / 2.0;
    float final_angle = -1.5 * PI - 2.0 * PI;

    float spiral_progress = pow(t, 0.5);

    float initial_radii[circle_count];
    for(int i = circle_count - 1; i >= 0; i--) {
        initial_radii[i] = 0.02 + 0.01 * float(circle_count - i - 1);
    }

    float final_radius = u_loading_radius;

    float angle_offset = 2.0 * PI / float(circle_count);

    float dist = 1e9;
    for(int i = 0; i < circle_count; i++) {
        float angle = mix(initial_angle, final_angle + angle_offset * float(i), spiral_progress);
        vec2 position = vec2(orbit_radius * cos(angle), orbit_radius * sin(angle));

        float radius = mix(initial_radii[i], final_radius, t * t);

        dist = min(dist, circleSDF(radius, uv, position));
    }
    return dist;
}

vec3 spiral_transition(vec2 uv, float t) {
    float time_offset = u_time - u_transition_start;
    const int circle_count = CIRCLE_COUNT;

    if(t < 0.6) {
        float normalizedT = t / 0.6;
        float orbit_radius = mix(u_orbit_radius, 0.0, normalizedT);

        circle circles[circle_count];
        float total = 0.0;
        for(int i = 0; i < circle_count; i++) {
            float initial_angle = -1.5 * PI - (2.0 * PI / float(circle_count)) * float(i) - PI * time_offset;
            float angle = -PI * normalizedT + initial_angle;
            circles[i].radius = u_loading_radius;
            circles[i].position = vec2(orbit_radius * cos(angle), orbit_radius * sin(angle));
            circles[i].color = vec4(1.0, 1.0, 1.0, 1.0);

            total += MetaBallSDF(circles[i], uv);
        }

        return total > 2.0 ? vec3(1.0, 1.0, 1.0) : vec3(0.0, 0.0, 0.0);
    } else {
        float normalizedT = (t - 0.6) / 0.4;
        float springT = normalizedT + springEffect(normalizedT, 0.15, 2.5, 4.0);
        springT = clamp(springT, 0.0, 1.0);
        float scaledRadius = mix(u_loading_radius+0.05, u_big_ball_size, springT);

        float dist = circleSDF(scaledRadius, uv, vec2(0.0, 0.0));
        float alpha = antialiasing(dist);

        return vec3(1.0 - alpha);
    }
}

float shrink_transition(vec2 uv, float t) {
    float scale = mix(u_big_ball_size, u_small_ball_size, t);
    float dist = circleSDF(scale, uv, vec2(0.0, 0.0));
    return dist;
}

float splash_particles(vec2 uv, float t) {
    // Active only during the burst phase
    if(t < 0.25 || t > 0.75) return 0.0;
    float nt = (t - 0.25) / 0.5; // 0..1 within burst window

    const int COUNT = 10;
    float dist = 1e9;
    for(int i = 0; i < COUNT; i++) {
        float fi = float(i);
        // Deterministic angle per particle
        float angle = fi * (2.0 * PI / float(COUNT)) +
                      fract(sin(fi * 127.1) * 43758.5) * 0.6;
        // Each particle has its own speed
        float speed = 0.18 + fract(sin(fi * 311.7) * 5342.1) * 0.18;
        vec2 pos = vec2(cos(angle), sin(angle)) * speed * nt;

        // Shrink and fade out
        float r = mix(0.018, 0.0, nt * nt);
        dist = min(dist, circleSDF(r, uv, pos));
    }
    // Fade out toward end
    float alpha = (1.0 - smoothstep(0.6, 1.0, nt));
    return dist < 0.0 ? alpha : 0.0;
}

vec3 split_transition(vec2 uv, float t) {
    // Quick inhale: small ball grows to big ball before splitting
    if(t < 0.15) {
        float nt = t / 0.15;
        float radius = mix(u_small_ball_size, u_big_ball_size, smoothstep(0.0, 1.0, nt));
        float dist = circleSDF(radius, uv, vec2(0.0, 0.0));
        return dist < 0.0 ? vec3(1.0) : vec3(0.0);
    } else if(t < 0.8) {
        const int circle_count = CIRCLE_COUNT;
        float adjusted_t = (t - 0.15) / 0.65;
        float smooth_t = smoothstep(0.0, 1.0, adjusted_t);
        float motion_t = mix(smooth_t, 1.0 - exp(-3.0 * smooth_t) * cos(2.5 * smooth_t * PI), 0.15);

        float ball_radius = mix(u_big_ball_size * 0.6, u_loading_radius, smoothstep(0.0, 1.0, adjusted_t));

        float SPLIT_DIST = 0.2;

        float dist = 0.0;
        for(int i = 0; i < circle_count; i++) {
            float final_x = (i < 2) ? -SPLIT_DIST : SPLIT_DIST;
            float final_y = ((i % 2) == 0) ? -SPLIT_DIST : SPLIT_DIST;
            vec2 position = vec2(0.0);

            if(adjusted_t < 0.5) {
                position = vec2(mix(0.0, final_x, motion_t * 2.0), 0.0);
            } else {
                float second_split_t = (adjusted_t - 0.5) / 0.5;
                position = vec2(final_x * 1.2, mix(0.0, final_y, smoothstep(0.0, 1.0, second_split_t)));
            }
            circle c = circle(position, ball_radius, vec4(1.0, 1.0, 1.0, 1.0));
            dist += MetaBallSDF(c, uv);
        }

        float splash = splash_particles(uv, t);
        return dist > 2.25 ? vec3(1.0) : vec3(splash);
    } else {
        circle circles[CIRCLE_COUNT];
        float nt = (t - 0.8) * 10.0 / 2.0;
        circles[0].position = vec2(0.2 * 1.12, mix(0.2 * 1.12, 0.0, nt));
        circles[1].position = vec2(mix(0.2 * 1.12, 0.0, nt), -0.2 * 1.12);
        circles[2].position = vec2(-0.2 * 1.12, mix(-0.2 * 1.12, 0.0, nt));
        circles[3].position = vec2(mix(-0.2 * 1.12, 0.0, nt), 0.2 * 1.12);
        float dist = 1e9;
        for(int i = 0; i < CIRCLE_COUNT; i++) {
            circles[i].radius = u_loading_radius;
            circles[i].color = vec4(1.0, 1.0, 1.0, 1.0);
            dist = min(dist, circleSDF(circles[i].radius, uv, circles[i].position));
        }
        return dist < 0.0 ? vec3(1.0) : vec3(0.0);
    }
}

vec3 listen_transition(vec2 uv, float t) {
    //add blur to the center circle with the radius u_small_ball_size
    float dist = circleSDF(u_small_ball_size, uv, vec2(0.0, 0.0));
    float alpha = antialiasing(dist);
    return vec3(1.0 - alpha);
}

// Message bubble target values
// Body: rounded rect, halfSize = u_small_ball_size * (1.8, 1.2), corner = u_small_ball_size * 0.45, center (0, 0.04)
// Tail: center (-u_small_ball_size*0.75, -u_small_ball_size*1.15), radius = u_small_ball_size*0.3

vec3 idle_to_message_transition(vec2 uv, float t) {
    float st = smoothstep(0.0, 1.0, t);

    // Body drifts up, morphs from circle to rounded rect
    vec2 body_pos  = mix(vec2(0.0, 0.0), vec2(0.0, 0.04), st);
    vec2 halfSize  = vec2(u_small_ball_size * 1.8, u_small_ball_size * 1.2);
    float cornerR  = u_small_ball_size * 0.45;
    float circleDist = circleSDF(u_small_ball_size, uv, body_pos);
    float rectDist   = roundedBoxSDF(uv - body_pos, halfSize, cornerR);
    float body     = mix(circleDist, rectDist, st);

    // Tail grows in after 30%
    float tail_t   = smoothstep(0.3, 1.0, t);
    float tail_r   = u_small_ball_size * 0.3 * tail_t;
    vec2  tail_pos = vec2(-u_small_ball_size * 0.75, -u_small_ball_size * 1.15);
    float tail     = circleSDF(tail_r, uv, tail_pos);

    float dist = smin(body, tail, 0.04 * tail_t);
    return vec3(1.0 - antialiasing(dist));
}

vec3 message_to_idle_transition(vec2 uv, float t) {
    float st = smoothstep(0.0, 1.0, t);

    // Body moves back to center, morphs rounded rect → circle
    vec2 body_pos  = mix(vec2(0.0, 0.04), vec2(0.0, 0.0), st);
    vec2 halfSize  = vec2(u_small_ball_size * 1.8, u_small_ball_size * 1.2);
    float cornerR  = u_small_ball_size * 0.45;
    float circleDist = circleSDF(u_small_ball_size, uv, body_pos);
    float rectDist   = roundedBoxSDF(uv - body_pos, halfSize, cornerR);
    float body     = mix(rectDist, circleDist, st);

    // Tail shrinks first (done by 60%)
    float tail_t   = 1.0 - smoothstep(0.0, 0.6, t);
    float tail_r   = u_small_ball_size * 0.3 * tail_t;
    vec2  tail_pos = vec2(-u_small_ball_size * 0.75, -u_small_ball_size * 1.15);
    float tail     = circleSDF(tail_r, uv, tail_pos);

    float dist = smin(body, tail, 0.04 * tail_t);
    return vec3(1.0 - antialiasing(dist));
}

//---------------------------------STATES---------------------------------
vec3 blank_state(vec2 uv) {
    return vec3(0.0, 0.0, 0.0);
}
vec3 loading_state(vec2 uv) {
    float t = u_time - u_state_start;
    const int circle_count = CIRCLE_COUNT;
    float orbit_radius = u_orbit_radius;
    circle circles[circle_count];
    float initial_angle[circle_count];
    for(int i = 0; i < circle_count; i++) {
        initial_angle[i] = -1.5 * PI - (2.0 * PI / float(circle_count)) * float(i);
    }
    float dist = 1e9;

    for(int i = 0; i < circle_count; i++) {
        float angle = initial_angle[i] - PI * t;
        circles[i].radius = u_loading_radius;
        circles[i].position = vec2(orbit_radius * cos(angle), orbit_radius * sin(angle));

        dist = min(dist, circleSDF(circles[i].radius, uv, circles[i].position));
    }
    float alpha = antialiasing(dist);
    return vec3(1.0 - alpha);
}

vec3 idle_state(vec2 uv) {
    float breath = 0.005 * sin(u_time * 0.8);
    float dist = circleSDF(u_small_ball_size + breath, uv, vec2(0.0, 0.0));
    float alpha = antialiasing(dist);
    return vec3(1.0 - alpha);
}

vec3 speaking_state(vec2 uv) {
    float angle = atan(uv.y, uv.x);

    float amp = u_amplitude;
    float speed = 5.0;

    float wave =
        amp * 0.04 * sin(4.0 * angle + u_time * 4.0 * speed) +
        amp * 0.03 * sin(7.0 * angle - u_time * 3.1 * speed) +
        amp * 0.02 * sin(11.0 * angle + u_time * 2.0 * speed);

    float pulse = u_big_ball_size + amp * 0.2 + wave;
    float dist = circleSDF(pulse, uv, vec2(0.0, 0.0));
    float alpha = antialiasing(dist);
    return vec3(1.0 - alpha);
}

vec3 listening_state(vec2 uv) {
    float t = u_time - u_state_start;
    float r = length(uv);

    // Solid center ball
    float dist = circleSDF(u_small_ball_size, uv, vec2(0.0, 0.0));
    float ball = 1.0 - antialiasing(dist);

    // Ripples: 3 rings expanding outward from ball, fading as they go
    float ripple = 0.0;
    float ripple_speed = 0.12;
    float ripple_interval = 0.7;
    float max_travel = 0.25;
    for(int i = 0; i < 3; i++) {
        float offset = float(i) * ripple_interval;
        float phase = mod(t - offset, ripple_interval * 3.0);
        float travel = phase * ripple_speed;
        if(travel > max_travel) continue;
        float ring_r = u_small_ball_size + max_travel - travel;
        float ring_fade = smoothstep(0.0, max_travel, travel);
        float ring_dist = abs(r - ring_r) - 0.003;
        float ring = (1.0 - antialiasing(ring_dist)) * ring_fade;
        ripple = max(ripple, ring);
    }

    float outside_ball = 1.0 - ball;
    return vec3(ball + ripple * outside_ball);
}

vec3 message_state(vec2 uv) {
    float breath = 0.004 * sin(u_time * 0.7);

    // Rounded rect body slightly above center
    vec2 body_pos = vec2(0.0, 0.04);
    vec2 halfSize = vec2(u_small_ball_size * 1.8, u_small_ball_size * 1.2) + vec2(breath);
    float cornerR = u_small_ball_size * 0.45;
    float body    = roundedBoxSDF(uv - body_pos, halfSize, cornerR);

    // Small tail at bottom-left
    vec2 tail_pos = vec2(-u_small_ball_size * 0.75, -u_small_ball_size * 1.15);
    float tail    = circleSDF(u_small_ball_size * 0.3, uv, tail_pos);

    float dist = smin(body, tail, 0.04);
    return vec3(1.0 - antialiasing(dist));
}

void main() {
    vec2 uv = (2.0 * gl_FragCoord.xy - u_resolution.xy) / u_resolution.y;
    vec3 color = vec3(0.0, 0.0, 0.0);

    float t = clamp((u_time - u_transition_start) / u_duration, 0.0, 1.0);

    if(u_state == -1) {
        // Transitions
        float dist;
        switch(u_transition_type) {
            case 0:
                dist = init_transition(uv, t);
                color = vec3(1.0 - antialiasing(dist));
                break;
            case 1:
                color = spiral_transition(uv, t);
                break;
            case 2:
                dist = shrink_transition(uv, t);
                color = vec3(1.0 - antialiasing(dist));
                break;
            case 3:
                color = split_transition(uv, t);
                break;
            case 4:
                color = listen_transition(uv, t);
                break;
            case 5:
                color = idle_to_message_transition(uv, t);
                break;
            case 6:
                color = message_to_idle_transition(uv, t);
                break;
            default:
                dist = 1e9;
                color = vec3(0.0, 0.0, 0.0);
        }
    } else {
        // States
        switch(u_state) {
            case 0:
                color = blank_state(uv);
                break;
            case 1:
                color = idle_state(uv);
                break;
            case 2:
                color = loading_state(uv);
                break;
            case 3:
                color = speaking_state(uv);
                break;
            case 4:
                color = listening_state(uv);
                break;
            case 5:
                color = message_state(uv);
                break;
            default:
                color = vec3(0.0, 0.0, 0.0);
        }
    }

    if(u_stop_session) {
        float progress = (u_time - u_stop_time);
        float t = progress * 1.5;
        vec3 col = vec3(0.0, 0.0, 0.0);
        vec2 uv = (2.0 * gl_FragCoord.xy - u_resolution.xy) / u_resolution.y;

        if(t <= 0.1) {
            col = vec3(1.0);
        } else if(t < 0.4) {
            float nt = (t - 0.1) * 10.0 / 3.0;
            float height = mix(1.0, 0.01, pow(nt, 2.0));
            float dist = abs(uv.y) - height;
            if(dist < 0.0)
                col = vec3(1.0);

            float glow = bloom(dist, 50.0);
            col += vec3(1.0, 1.0, 1.0) * glow;
        } else {
            float nt = (t - 0.4) * 10.0 / 6.0;
            float width = mix(2.0, 0.0, sqrt(nt));
            float height = 0.01;
            float dist = rectangleSDF(height, width, uv);
            if(dist < 0.0)
                col = vec3(1.0);

            float glow = bloom(dist, 100.0);
            col += vec3(1.0, 1.0, 1.0) * glow;

            float sphereRadius;
            if(nt < 0.5) {
                float factor = nt * 2.0;
                sphereRadius = mix(0.0, 0.2, pow(factor, 4.0));
            } else {
                float factor = (nt - 0.5) * 2.0;
                sphereRadius = mix(0.2, 0.0, pow(factor, 4.0));
            }
            float sphereDist = length(uv) - sphereRadius;
            float sphereGlow = bloom(sphereDist, 15.0);

            col += vec3(1.0, 1.0, 1.0) * sphereGlow;
        }
        color = col;
    }

    gl_FragColor = vec4(color, u_opacity);
}